import { theme } from './theme';

export { foundation } from './foundation';
export { glassButtonSx, inverseButtonSx, muiTheme } from './mui';
export { palette } from './palette';
export { theme } from './theme';
export type { Theme } from './theme';

/** Publish the theme as CSS variables for stylesheets that still use var(--token). */
export function applyTheme(): void {
  const { color, font, space, radius, shadow, gradient, alpha, blur, layout, motion, z } = theme;

  const cssVariables: Record<string, string> = {
    '--color-black': color.black,
    '--color-white': color.white,
    '--color-off-white': color.offWhite,
    '--color-studio': color.studio,
    '--color-blue': color.blue,
    '--color-raised': color.raised,
    '--color-ink': color.ink,
    '--color-blue-deep': color.blueDeep,
    '--color-blue-bright': color.blueBright,
    '--color-blue-mist': color.blueMist,
    '--color-blue-soft': color.blueSoft,
    '--color-text': color.text,
    '--color-text-secondary': color.textSecondary,
    '--color-text-muted': color.textMuted,
    '--color-text-inverse': color.textInverse,
    '--color-text-inverse-muted': color.textInverseMuted,
    '--color-surface': color.surface,
    '--color-surface-muted': color.surfaceMuted,
    '--color-surface-inverse': color.surfaceInverse,
    '--color-navbar': color.navbar,
    '--color-border': color.border,
    '--color-border-subtle': color.borderSubtle,
    '--color-border-strong': color.borderStrong,
    '--color-border-inverse': color.borderInverse,
    '--color-success': color.success,
    '--color-success-bg': color.successBg,
    '--color-success-border': color.successBorder,
    '--color-error': color.error,
    '--color-error-bg': color.errorBg,
    '--color-error-border': color.errorBorder,
    '--color-error-hover': color.errorHover,
    '--color-warning': color.warning,
    '--color-warning-bg': color.warningBg,

    '--font-family-base': font.family.base,
    '--font-family-mono': font.family.mono,
    '--font-size-xs': font.size.xs,
    '--font-size-sm': font.size.sm,
    '--font-size-base': font.size.base,
    '--font-size-lg': font.size.lg,
    '--font-size-xl': font.size.xl,
    '--font-size-2xl': font.size.xl2,
    '--font-size-3xl': font.size.xl3,
    '--font-size-4xl': font.size.xl4,
    '--font-size-5xl': font.size.xl5,
    '--font-size-6xl': font.size.xl6,
    '--font-size-lead': font.size.lead,
    '--font-size-display': font.size.display,
    '--font-size-h1': font.size.h1,
    '--font-size-h2': font.size.h2,
    '--font-size-h3': font.size.h3,
    '--font-weight-normal': font.weight.normal,
    '--font-weight-medium': font.weight.medium,
    '--font-weight-semibold': font.weight.semibold,
    '--font-weight-bold': font.weight.bold,
    '--font-weight-extrabold': font.weight.extrabold,
    '--line-height-none': font.lineHeight.none,
    '--line-height-tight': font.lineHeight.tight,
    '--line-height-snug': font.lineHeight.snug,
    '--line-height-normal': font.lineHeight.normal,
    '--line-height-relaxed': font.lineHeight.relaxed,
    '--line-height-loose': font.lineHeight.loose,
    '--letter-spacing-tight': font.letterSpacing.tight,
    '--letter-spacing-normal': font.letterSpacing.normal,
    '--letter-spacing-wide': font.letterSpacing.wide,

    '--spacing-0': space[0],
    '--spacing-1': space[1],
    '--spacing-2': space[2],
    '--spacing-3': space[3],
    '--spacing-4': space[4],
    '--spacing-5': space[5],
    '--spacing-6': space[6],
    '--spacing-8': space[8],
    '--spacing-10': space[10],
    '--spacing-12': space[12],
    '--spacing-16': space[16],
    '--spacing-20': space[20],
    '--spacing-24': space[24],

    '--radius-sm': radius.sm,
    '--radius-md': radius.md,
    '--radius-lg': radius.lg,
    '--radius-xl': radius.xl,
    '--radius-2xl': radius.xl2,
    '--radius-full': radius.full,

    '--shadow-xs': shadow.xs,
    '--shadow-sm': shadow.sm,
    '--shadow-md': shadow.md,
    '--shadow-lg': shadow.lg,
    '--shadow-xl': shadow.xl,
    '--shadow-blue': shadow.blue,
    '--shadow-blue-lg': shadow.blueLg,
    '--shadow-button': shadow.button,
    '--shadow-focus': shadow.focus,
    '--shadow-text': shadow.text,

    '--gradient-brand': gradient.brand,
    '--gradient-brand-horizontal': gradient.brandHorizontal,
    '--gradient-text-on-dark': gradient.textOnDark,
    '--gradient-hero': gradient.hero,
    '--gradient-band': gradient.band,
    '--gradient-hero-glow': gradient.heroGlow,
    '--gradient-surface': gradient.surface,
    '--gradient-card': gradient.card,

    '--alpha-white-04': alpha.white[4],
    '--alpha-white-10': alpha.white[10],
    '--alpha-white-15': alpha.white[15],
    '--alpha-white-20': alpha.white[20],
    '--alpha-white-30': alpha.white[30],
    '--alpha-white-50': alpha.white[50],
    '--alpha-white-60': alpha.white[60],
    '--alpha-white-70': alpha.white[70],
    '--alpha-white-80': alpha.white[80],
    '--alpha-white-90': alpha.white[90],
    '--alpha-white-95': alpha.white[95],
    '--alpha-black-05': alpha.black[5],
    '--alpha-black-08': alpha.black[8],
    '--alpha-black-10': alpha.black[10],
    '--alpha-black-12': alpha.black[12],
    '--alpha-black-22': alpha.black[22],
    '--alpha-black-35': alpha.black[35],
    '--alpha-blue-10': alpha.blue[10],
    '--alpha-blue-12': alpha.blue[12],
    '--alpha-blue-15': alpha.blue[15],
    '--alpha-blue-20': alpha.blue[20],
    '--alpha-blue-25': alpha.blue[25],
    '--alpha-blue-32': alpha.blue[32],
    '--alpha-blue-45': alpha.blue[45],
    '--alpha-off-white-95': alpha.offWhite95,
    '--alpha-ink-22': alpha.ink22,

    '--blur-sm': blur.sm,
    '--blur-md': blur.md,
    '--blur-lg': blur.lg,

    '--container-max-width': layout.containerMaxWidth,
    '--container-padding': layout.containerPadding,
    '--container-wide': layout.containerWide,
    '--measure-narrow': layout.measureNarrow,
    '--measure': layout.measure,
    '--measure-wide': layout.measureWide,
    '--section-padding-y': layout.sectionPaddingY,
    '--section-gap': layout.sectionGap,
    '--nav-height': layout.navHeight,

    '--duration-fast': motion.durationFast,
    '--duration-base': motion.durationBase,
    '--duration-slow': motion.durationSlow,
    '--duration-deliberate': motion.durationDeliberate,
    '--ease-standard': motion.easeStandard,
    '--ease-out': motion.easeOut,
    '--transition-fast': motion.transitionFast,
    '--transition-base': motion.transitionBase,
    '--transition-slow': motion.transitionSlow,
    '--transition-lift': motion.transitionLift,

    '--z-base': z.base,
    '--z-dropdown': z.dropdown,
    '--z-sticky': z.sticky,
    '--z-fixed': z.fixed,
    '--z-modal': z.modal,
    '--z-popover': z.popover,
    '--z-tooltip': z.tooltip,

    '--color-primary': color.blue,
    '--color-primary-light': color.blueBright,
    '--color-primary-dark': color.blueDeep,
    '--color-primary-accent': color.blueBright,
    '--color-primary-hover': color.blueDeep,
    '--color-dark': color.raised,
    '--color-gray': color.textSecondary,
    '--color-light-gray': color.border,
    '--orca-black': color.black,
    '--orca-dark': color.raised,
    '--orca-gray': color.textSecondary,
    '--orca-light-gray': color.border,
    '--orca-white': color.white,
    '--orca-off-white': color.offWhite,
    '--ocean-blue': color.blue,
    '--ocean-blue-light': color.blueBright,
    '--ocean-blue-dark': color.blueDeep,
    '--ocean-blue-accent': color.blueBright,
  };

  const root = document.documentElement;
  Object.entries(cssVariables).forEach(([token, value]) => {
    root.style.setProperty(token, value);
  });
}
